// Botón «Comprobante» de Mis ventas y Ventas: abre las líneas congeladas de una venta completada con
// su forma de pago y, si la venta trae el reparto, lo financiado y lo de contado.
import { useState, type ReactNode } from 'react';
import { Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { saleReceipt, type SaleReceipt } from '@/shared/lib/format/saleReceipt';
import type { SaleLine, SalePromotionInfo } from '@/api/services/retail-wizard/contract';
import { toSlotSale, type SaleSlots } from './saleSlots';
import { SaleBenefitsOutlet } from './SaleSlotOutlets';
import { SaleLineCard } from './SaleLineCard';
import { SaleReceiptTicket } from './SaleReceiptTicket';

/** Lo que el comprobante lee de la venta; sirve igual para Mis ventas y para Ventas. */
export interface SaleReceiptSale extends SalePromotionInfo {
    saleId: string;
    folio: string;
    soldAt: string;
    status: string;
    customerName: string | null;
    storeName: string | null;
    sellerName: string | null;
    lines?: SaleLine[];
    /** Lo que entró a la caja. */
    amountCents?: number;
    /** Forma de pago y método de cobro: «Contado · Efectivo», «SureKeep · enganche en efectivo». */
    paymentSummary?: string | null;
}

const NOT_COMPLETED_NOTE = 'El comprobante solo existe para una venta completada.';
const NO_LINES_NOTE = 'Esta venta es anterior a las líneas: no tiene comprobante por producto.';

function money(cents: number): string {
    return formatMXN(cents / 100);
}

export function SaleReceiptButton({
    sale,
    className,
    slots = {},
    icon,
    variant = 'dialog',
    brand = 'SureKeep',
}: {
    sale: SaleReceiptSale;
    className?: string;
    slots?: SaleSlots;
    /** Ícono antes del rótulo del botón. */
    icon?: ReactNode;
    /** `ticket` es el comprobante con forma de ticket del portal del vendedor. */
    variant?: 'dialog' | 'ticket';
    /** Marca del encabezado del ticket. */
    brand?: string;
}) {
    const [open, setOpen] = useState(false);
    const receipt = saleReceipt(sale.lines);
    const completed = sale.status === 'COMPLETED';
    const disabledNote = !completed ? NOT_COMPLETED_NOTE : receipt ? undefined : NO_LINES_NOTE;

    return (
        <>
            <button
                type="button"
                disabled={disabledNote !== undefined}
                title={disabledNote}
                onClick={() => {
                    setOpen(true);
                }}
                className={cn(
                    'hover:bg-accent disabled:cursor-not-allowed disabled:text-muted-foreground disabled:opacity-50',
                    className,
                )}
            >
                {icon}
                Comprobante
            </button>
            {receipt && variant === 'ticket' && (
                <SaleReceiptTicket
                    open={open}
                    onClose={() => {
                        setOpen(false);
                    }}
                    sale={sale}
                    receipt={receipt}
                    slots={slots}
                    brand={brand}
                />
            )}
            {receipt && variant === 'dialog' && (
                <Modal
                    isOpen={open}
                    onClose={() => {
                        setOpen(false);
                    }}
                    size="md"
                    title={
                        <span>
                            Comprobante{' '}
                            <span className="font-mono text-sm text-muted-foreground">
                                {sale.folio}
                            </span>
                        </span>
                    }
                    description={formatDateMx(sale.soldAt)}
                >
                    <ReceiptBody sale={sale} receipt={receipt} slots={slots} />
                </Modal>
            )}
        </>
    );
}

function ReceiptBody({
    sale,
    receipt,
    slots,
}: {
    sale: SaleReceiptSale;
    receipt: SaleReceipt;
    slots: SaleSlots;
}) {
    const slotSale = toSlotSale(
        sale,
        receipt.rows.map((r) => r.line),
    );
    return (
        <div className="flex flex-col gap-3 text-[12px]">
            <dl className="grid grid-cols-3 gap-x-2 gap-y-1">
                <Fact label="Cliente" value={sale.customerName} />
                <Fact label="Tienda" value={sale.storeName} />
                <Fact label="Vendedor" value={sale.sellerName} />
            </dl>

            <ul aria-label="Líneas del comprobante" className="flex flex-col gap-1.5">
                {receipt.rows.map((row) => (
                    <SaleLineCard
                        key={row.key}
                        row={row}
                        sale={slotSale}
                        linePromotion={slots.linePromotion}
                        giftName={slots.giftName}
                    />
                ))}
            </ul>

            <dl className="rounded-md border border-border bg-muted/35 px-3 py-2">
                {sale.paymentSummary && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                        <dt className="text-muted-foreground">Forma de pago</dt>
                        <dd data-testid="receipt-payment">{sale.paymentSummary}</dd>
                    </div>
                )}
                <Total label="Total de la venta" cents={receipt.totalCents} strong />
                {receipt.totals && (
                    <>
                        <Total label="Financiado" cents={receipt.totals.financedCents} />
                        <Total label="De contado" cents={receipt.totals.cashCents} />
                    </>
                )}
                {sale.amountCents !== undefined && (
                    <Total label="Total cobrado" cents={sale.amountCents} strong />
                )}
            </dl>

            <SaleBenefitsOutlet slot={slots.saleBenefits} sale={slotSale} />
        </div>
    );
}

function Fact({ label, value }: { label: string; value: string | null }) {
    return (
        <>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </dt>
            <dd className="col-span-2">{value ?? '—'}</dd>
        </>
    );
}

function Total({ label, cents, strong }: { label: string; cents: number; strong?: boolean }) {
    return (
        <div className="flex items-baseline justify-between gap-2 py-0.5">
            <dt className={strong ? 'font-semibold' : 'text-muted-foreground'}>{label}</dt>
            <dd className={cn('tabular-nums', strong && 'font-semibold')}>{money(cents)}</dd>
        </div>
    );
}
